<script setup>
import MiniStatisticsCard from "@/examples/Cards/MiniStatisticsCard.vue";
import Accounts from "../components/Accounts.vue";
import Cards from "../components/Cards.vue";
import Transactions from "../components/Transactions.vue";
import { computed, onBeforeMount, onMounted, onUnmounted, onUpdated, ref } from "vue";
import router from "../router";
import cookie from "../assets/js/cookies.js";
import validSession from "../assets/js/valid-session.js";
import { useStore } from "vuex";
import axios from "axios";
import SkeletonLoad from "../components/SkeletonLoad.vue";
import { toast } from "vue3-toastify";
import DonutChart from "../components/DonutChart.vue";
import Masonry from "masonry-layout";
import BarChart from "../components/BarChart.vue";
import ObjetivesRowDashboard from "../components/ObjetivesRowDashboard.vue";
import { activateDarkMode, deactivateDarkMode } from "@/assets/js/dark-mode";
import emitter from "../assets/js/eventBus.js";
import bootstrap from "bootstrap/dist/js/bootstrap";
import { useI18n } from "vue-i18n";

const store = useStore();
const loaded_itens = [];
const setSidebarType = (type) => store.commit("sidebarType", type);
const darkMode = computed(() => store.state.darkMode);
const { t } = useI18n();

const meses = [
  t("message.global.month.jan"),
  t("message.global.month.feb"),
  t("message.global.month.mar"),
  t("message.global.month.apr"),
  t("message.global.month.may"),
  t("message.global.month.jun"),
  t("message.global.month.jul"),
  t("message.global.month.aug"),
  t("message.global.month.sep"),
  t("message.global.month.oct"),
  t("message.global.month.nov"),
  t("message.global.month.dec"),
];

const formatMoney = (v) => {
  let total = parseFloat(v || 0);
  return total.toLocaleString("pt-br", {
    style: "currency",
    currency: "BRL",
  });
};

const activeStatSlide = ref(0);
const statsCarouselRef = ref(null);

const updateActiveStatSlide = () => {
  const el = statsCarouselRef.value;
  if (!el) return;

  const slides = el.querySelectorAll(".stats-slide");
  if (!slides.length) return;

  const scrollLeft = el.scrollLeft;
  let closestIndex = 0;
  let closestDistance = Infinity;

  slides.forEach((slide, index) => {
    const distance = Math.abs(slide.offsetLeft - scrollLeft);
    if (distance < closestDistance) {
      closestDistance = distance;
      closestIndex = index;
    }
  });

  activeStatSlide.value = closestIndex;
};

const goToStatSlide = (index) => {
  const el = statsCarouselRef.value;
  if (!el) return;
  const slides = el.querySelectorAll(".stats-slide");
  const target = slides[index];
  if (!target) return;

  el.scrollTo({
    left: target.offsetLeft,
    behavior: "smooth",
  });
  activeStatSlide.value = index;
};


const goToSection = (targetId, options = {}) => {
  const { tabId = null, behavior = "smooth", block = "start" } = options;

  if (tabId) {
    const tabButton = document.getElementById(tabId);
    if (tabButton) {
      const tab = bootstrap.Tab.getOrCreateInstance(tabButton);
      tab.show();
    }
  }

  const target = document.getElementById(targetId);
  if (target) {
    setTimeout(() => {
      target.scrollIntoView({ behavior, block });
    }, tabId ? 180 : 0);
  }
};

const handleInsightClick = (insight) => {
  if (!insight) return;

  if (typeof insight.action === "function") {
    insight.action();
    return;
  }

  if (insight.targetId) {
    goToSection(insight.targetId, { tabId: insight.tabId, block: "center" });
  }
};

const parseTransactionDate = (value) => {
  if (!value) return null;

  if (value instanceof Date) return Number.isNaN(value.getTime()) ? null : value;

  const normalized = String(value).trim();
  if (!normalized) return null;

  if (/^\d{4}-\d{2}-\d{2}$/.test(normalized)) {
    const [year, month, day] = normalized.split("-").map(Number);
    return new Date(year, month - 1, day);
  }

  if (/^\d{2}\/\d{2}\/\d{4}$/.test(normalized)) {
    const [day, month, year] = normalized.split("/").map(Number);
    return new Date(year, month - 1, day);
  }

  const parsed = new Date(normalized);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

const daysBetween = (dateA, dateB) => {
  if (!dateA || !dateB) return 0;
  const dayMs = 1000 * 60 * 60 * 24;
  return Math.round((dateA.getTime() - dateB.getTime()) / dayMs);
};

const insights = computed(() => {
  const list = [];
  const now = new Date();
  const expenses = store.state.transactions_out || [];
  const revenues = store.state.transactions_in || [];
  const currentBalance = parseFloat(store.state.values?.balance || 0);
  const futureBalance = parseFloat(store.state.values?.balance_future || 0);

  const unpaidExpenses = expenses.filter((item) => item.status === "Unpaid");
  const overdue = unpaidExpenses.filter((item) => {
    const txDate = parseTransactionDate(item.date);
    if (!txDate) return false;
    txDate.setHours(0, 0, 0, 0);
    return txDate < new Date(now.getFullYear(), now.getMonth(), now.getDate());
  });

  const overdueTotal = overdue.reduce((sum, item) => sum + parseFloat(item.amount || 0), 0);

  if (overdue.length > 0) {
    list.push({
      title: t('message.dashboard_insights.overdue_title'),
      body: `${overdue.length} item${overdue.length > 1 ? "s" : ""} somando ${formatMoney(overdueTotal)}.`,
      tag: "Ver despesas",
      tone: "danger",
      priority: 100,
      targetId: "out-transactions",
      tabId: "out-transactions-tab",
    });
  }

  if (futureBalance < 0) {
    list.push({
      title: "Saldo previsto em queda",
      body: `O saldo previsto cai para ${formatMoney(futureBalance)}. Vale reforçar entradas ou rever saídas.`,
      tag: "Ver fluxo",
      tone: "danger",
      priority: 95,
      targetId: "receitas-despesas",
    });
  }

  const projectedExpenses = parseFloat(store.state.values?.out || 0);
  const projectedRevenues = parseFloat(store.state.values?.in || 0);
  const projectedDifference = projectedExpenses - projectedRevenues;

  if (projectedExpenses > 0 && projectedDifference > 0) {
    list.push({
      title: "Saídas acima das entradas",
      body: `As despesas previstas do mês estão ${formatMoney(projectedDifference)} acima das receitas previstas.`,
      tag: "Ver receitas",
      tone: "warning",
      priority: 88,
      targetId: "receitas-despesas",
    });
  }

  const categories = store.state.categoriesChartData || [];
  if (categories.length > 0) {
    const sortedCategories = [...categories].sort((a, b) => parseFloat(b.amount || 0) - parseFloat(a.amount || 0));
    const top = sortedCategories[0];
    const totalCategories = sortedCategories.reduce((sum, item) => sum + parseFloat(item.amount || 0), 0);
    const topShare = totalCategories > 0 ? (parseFloat(top.amount || 0) / totalCategories) * 100 : 0;

    if (top) {
      list.push({
        title: topShare >= 40 ? "Categoria concentrando gastos" : "Maior foco de gasto",
        body:
          topShare >= 40
            ? `${top.name} representa ${topShare.toFixed(0)}% das despesas categorizadas do mês.`
            : `${top.name} lidera o mês com ${formatMoney(top.amount)}.`,
        tag: "Analisar categorias",
        tone: "warning",
        priority: topShare >= 40 ? 82 : 72,
        targetId: "despesas-categoria",
      });
    }
  }

  const upcomingWeek = unpaidExpenses.filter((item) => {
    const txDate = parseTransactionDate(item.date);
    if (!txDate) return false;
    const diff = daysBetween(txDate, now);
    return diff >= 0 && diff <= 7;
  });
  const upcomingWeekTotal = upcomingWeek.reduce((sum, item) => sum + parseFloat(item.amount || 0), 0);

  if (upcomingWeek.length >= 3 || upcomingWeekTotal > Math.max(currentBalance, 0) * 0.5) {
    list.push({
      title: "Próximos dias mais apertados",
      body: `${upcomingWeek.length} lançamento${upcomingWeek.length > 1 ? "s vencem" : " vence"} nos próximos 7 dias, somando ${formatMoney(upcomingWeekTotal)}.`,
      tag: "Organizar pagamentos",
      tone: "info",
      priority: 78,
      targetId: "out-transactions",
      tabId: "out-transactions-tab",
    });
  }

  const paidExpenses = expenses.filter((item) => item.status === "Paid");
  const recentPaidExpenses = paidExpenses.filter((item) => {
    const txDate = parseTransactionDate(item.date);
    if (!txDate) return false;
    const diff = daysBetween(now, txDate);
    return diff >= 0 && diff <= 10;
  });
  const olderPaidExpenses = paidExpenses.filter((item) => {
    const txDate = parseTransactionDate(item.date);
    if (!txDate) return false;
    const diff = daysBetween(now, txDate);
    return diff > 10 && diff <= 20;
  });

  const recentAverage =
    recentPaidExpenses.length > 0
      ? recentPaidExpenses.reduce((sum, item) => sum + parseFloat(item.amount || 0), 0) / recentPaidExpenses.length
      : 0;

  const olderAverage =
    olderPaidExpenses.length > 0
      ? olderPaidExpenses.reduce((sum, item) => sum + parseFloat(item.amount || 0), 0) / olderPaidExpenses.length
      : 0;

  if (
    recentPaidExpenses.length >= 3 &&
    olderPaidExpenses.length >= 3 &&
    olderAverage > 0 &&
    recentAverage > olderAverage * 1.35
  ) {
    list.push({
      title: "Gastos aceleraram recentemente",
      body: `O ticket médio das despesas pagas subiu de ${formatMoney(olderAverage)} para ${formatMoney(recentAverage)} nos lançamentos mais recentes.`,
      tag: "Revisar despesas",
      tone: "warning",
      priority: 80,
      targetId: "out-transactions",
      tabId: "out-transactions-tab",
    });
  }

  const revenuesInMonth = revenues.filter((item) => {
    const txDate = parseTransactionDate(item.date);
    if (!txDate) return false;
    const diff = daysBetween(now, txDate);
    return diff >= 0 && diff <= 30;
  });

  const paidRevenueTotal = revenuesInMonth
    .filter((item) => item.status === "Paid")
    .reduce((sum, item) => sum + parseFloat(item.amount || 0), 0);

  const plannedRevenueTotal = revenuesInMonth
    .filter((item) => item.status === "Unpaid")
    .reduce((sum, item) => sum + parseFloat(item.amount || 0), 0);

  if (plannedRevenueTotal > paidRevenueTotal * 1.2 && plannedRevenueTotal > 0) {
    list.push({
      title: "Receitas ainda dependem do previsto",
      body: `${formatMoney(plannedRevenueTotal)} ainda estão pendentes de entrada, acima do que já entrou no período.`,
      tag: "Ver receitas",
      tone: "info",
      priority: 74,
      targetId: "in-transactions",
      tabId: "in-transactions-tab",
    });
  }

  if (!list.length) {
    list.push({
      title: "Tudo sob controle",
      body: "Seu mês está equilibrado. Continue acompanhando o fluxo.",
      tag: "Ver painel",
      tone: "success",
      priority: 10,
      targetId: "minhas-contas",
    });
  }

  return list
    .sort((a, b) => (b.priority || 0) - (a.priority || 0))
    .slice(0, 3);
});

onBeforeMount(() => {
  validSession.isLogged();

  const session = cookie.get("user_agive_session");
  const day = new Date().getDate();
  const month = new Date().getMonth() + 1;
  const year = new Date().getFullYear();

  store.state.current_date =
    year + "-" + (month < 10 ? "0" + month : month) + "-" + (day < 10 ? "0" + day : day);

  store.state.selectedYear = year;
  store.state.selectedMonth = {
    index: new Date().getMonth(),
    number: new Date().getMonth() + 1,
    display: meses[new Date().getMonth()],
  };

  store.state.loading_values = true;
  store.state.values = {
    balance: 0,
    balance_future: 0,
    in: 0,
    in_payed: 0,
    out: 0,
    out_payed: 0,
    cc: 0,
    cc_payed: 0,
  };

  store.state.loading_accounts = true;
  store.state.accounts = [];
  store.state.loading_cards = true;
  store.state.cards_dashboard = [];
  store.state.loading_objetives = true;
  store.state.objetives = [];
  store.state.loading_transactions = true;
  store.state.transactions_in = [];
  store.state.transactions_out = [];
  store.state.loading_categoriesChart = true;
  store.state.categoriesChartData = [];

  store.state.categoriesChart = {
    chartData: {
      labels: ["Aguardando"],
      datasets: [
        {
          backgroundColor: ["#41B883"],
          data: [1],
        },
      ],
    },
    chartOptions: {
      responsive: true,
      maintainAspectRatio: false,
    },
  };

  store.state.InOutChart = {
    chartData: {
      labels: ["Janeiro"],
      datasets: [
        {
          label: t("message.global.revenue"),
          backgroundColor: "#2dce89",
          data: [0],
        },
        {
          label: t("message.global.expense"),
          backgroundColor: "#f5365c",
          data: [0],
        },
      ],
    },
    chartOptions: {
      responsive: true,
      maintainAspectRatio: false,
    },
  };

  store.state.InOutAnnualChartYear = year;
  store.state.loading_InOutAnnualChart = true;
  store.state.InOutAnnualChart = {
    chartData: {
      labels: ["Janeiro"],
      datasets: [
        {
          label: t("message.global.revenue"),
          backgroundColor: "#2dce89",
          data: [0],
        },
        {
          label: t("message.global.expense"),
          backgroundColor: "#f5365c",
          data: [0],
        },
      ],
    },
    chartOptions: {
      responsive: true,
      maintainAspectRatio: false,
    },
  };

  store.state.welcomeMessage = "";

  if (session !== null) {
    getValues();
    getAccounts();
    getCards();
    getTransaction();
    getCategories();
    getObjetives();
    getUserData();
    getValuesAnnual();
  }
});

const reload_data = () => {
  getValues();
  getAccounts();
  getCards();
  getTransaction();
  getCategories();
  getObjetives();
  getUserData();
  getValuesAnnual();
};

onMounted(() => {
  emitter.on("load-data", reload_data);

  const session = cookie.get("user_agive_session");
  const notification = cookie.get("user_agive_notification");

  if (session == null) {
    router.push({ name: "Login" });
  }

  if (notification != null) {
    alert(notification);
  }

  if (isMobile()) {
    const message = { action: "getToken", session: session };
    if (window.ReactNativeWebView) {
      window.ReactNativeWebView.postMessage(JSON.stringify(message));
    }
  }
});

onUpdated(() => {
  if (loaded_itens.length >= 7) {
    if (!isMobile()) {
      const row = document.querySelector(".mansory-row");
      if (row) {
        new Masonry(row, {
          percentPosition: true,
          horizontalOrder: true,
        });
      }
    }
  }
});

onUnmounted(() => {
  emitter.off("load-data", reload_data);
});

const getValues = async () => {
  const period =
    store.state.selectedYear +
    "-" +
    (store.state.selectedMonth.number < 10
      ? "0" + store.state.selectedMonth.number
      : store.state.selectedMonth.number);

  axios
    .get("dashboard/values?period=" + period)
    .then((res) => {
      if (res.status === 200) {
        store.state.values = {
          balance: res.data.balance || 0,
          balance_future: res.data.balance_future || 0,
          in: res.data.in || 0,
          in_payed: res.data.in_payed || 0,
          out: res.data.out || 0,
          out_payed: res.data.out_payed || 0,
          cc: res.data.cc || 0,
          cc_payed: res.data.cc_payed || 0,
        };

        store.state.InOutChart = {
          chartData: {
            labels: [store.state.selectedMonth.display],
            datasets: [
              {
                label: t("message.global.revenue"),
                backgroundColor: "#2dce89",
                borderRadius: 10,
                data: [res.data.in || 0],
              },
              {
                label: t("message.global.expense"),
                backgroundColor: "#f5365c",
                borderRadius: 10,
                data: [res.data.out || 0],
              },
            ],
          },
          chartOptions: {
            responsive: true,
            maintainAspectRatio: false,
            plugins: {
              tooltip: {
                callbacks: {
                  label: function (context) {
                    let label = context.dataset.label || "";
                    if (label) label += ": ";
                    if (context.raw !== null) {
                      label += new Intl.NumberFormat(t("message.global.date_language"), {
                        style: "currency",
                        currency: "BRL",
                      }).format(context.raw);
                    }
                    return label;
                  },
                },
              },
              legend: {
                display: true,
                labels: {
                  color: darkMode.value ? "white" : "black",
                  usePointStyle: true,
                },
              },
              datalabels: {
                display: false,
              },
            },
          },
        };

        store.state.loading_values = false;
        loaded_itens.push("Values");
      }
    })
    .catch((err) => console.error(err));
};

const getAccounts = async () => {
  axios
    .get("/dashboard/accounts")
    .then((res) => {
      if (res.status === 200) {
        store.state.accounts = res.data;
        store.state.loading_accounts = false;
        loaded_itens.push("Accounts");
      }
    })
    .catch((err) => console.error(err));
};

const getCards = async () => {
  const period =
    store.state.selectedYear +
    "-" +
    (store.state.selectedMonth.number < 10
      ? "0" + store.state.selectedMonth.number
      : store.state.selectedMonth.number);

  axios
    .get("/dashboard/cards?period=" + period)
    .then((res) => {
      if (res.status === 200) {
        store.state.cards_dashboard = res.data;
        store.state.loading_cards = false;
        loaded_itens.push("Cards");
      }
    })
    .catch((err) => console.error(err));
};

const getTransaction = async () => {
  axios
    .get("dashboard/transactions")
    .then((res) => {
      if (res.status === 200) {
        store.state.transactions_in = res.data.in;
        store.state.transactions_out = res.data.out;
        store.state.loading_transactions = false;
        loaded_itens.push("Transactions");
      }
    })
    .catch((err) => console.error(err));
};

const payTransation = (transaction) => {
  if (transaction.type !== "transferência") {
    emitter.emit("set-type", { data: transaction.type });

    store.state.transaction_value.value = formatMoney(transaction.amount);

    store.state.transation = {
      type: transaction.type,
      description: transaction.description,
      amount: formatMoney(transaction.amount),
      date: transaction.date,
      account:
        transaction.account_id > 0
          ? transaction.account_id + "-bank"
          : transaction.card_id + "-card",
      account_name: transaction.account_name,
      account_destiny:
        transaction.account_destiny_id > 0
          ? transaction.account_destiny_id + "-bank"
          : "",
      account_destiny_name: transaction.account_destiny_name,
      category: transaction.category_id,
      category_name: transaction.category_name,
      category_icon: transaction.category_icon,
      category_color: transaction.category_color,
      recurrence_checked: false,
      recurrence: transaction.recurrence,
      recurrence_period: "",
      initial_installment: 1,
      current_installment: transaction.current_installment,
      end_installment: transaction.end_installment,
      payed: "Paid",
      id: transaction.id,
      transaction_id: transaction.transaction_id,
      invoice_id: transaction.invoice_id,
      card_id: transaction.card_id,
      howUpdate: "just_this",
      amount_paid: transaction.amount_paid,
      ammount_unpaid: transaction.ammount_unpaid,
      observation: transaction.observation,
    };

    openModal("payTransaction");
  } else {
    const url = "/transactions/pay/" + transaction.id;
    const data = new FormData();
    data.append("status", transaction.status === "Paid" ? "Unpaid" : "Paid");

    axios
      .post(url, data)
      .then((res) => {
        if (!res.data.error) {
          toast("Transação atualizada com sucesso!", {
            autoClose: 1000,
            theme: "auto",
            type: "success",
            transition: "slide",
          });
          getTransaction(true, transaction.id);
          clearFields();
        }
      })
      .catch((err) => console.error(err));
  }
};

const openModal = (id) => {
  const modal = bootstrap.Modal.getOrCreateInstance(document.getElementById(id));
  modal.show();
};

const getCategories = async () => {
  const period =
    store.state.selectedYear +
    "-" +
    (store.state.selectedMonth.number < 10
      ? "0" + store.state.selectedMonth.number
      : store.state.selectedMonth.number);

  axios
    .get("dashboard/categories?period=" + period)
    .then((res) => {
      if (res.status === 200) {
        const chart_lables = [];
        const chart_values = [];
        const chart_colors = [];
        let total = 0;

        res.data.forEach((value) => {
          chart_lables.push(value.name);
          chart_colors.push(value.color);
          chart_values.push(value.amount);
          total += parseFloat(value.amount || 0);
        });

        store.state.categoriesChart = {
          chartData: {
            labels: chart_lables,
            datasets: [
              {
                data: chart_values,
                backgroundColor: chart_colors,
              },
            ],
          },
          chartOptions: {
            responsive: true,
            maintainAspectRatio: true,
            plugins: {
              legend: {
                display: true,
                position: "bottom",
                labels: {
                  color: darkMode.value ? "white" : "black",
                  usePointStyle: true,
                },
              },
              tooltip: {
                callbacks: {
                  label: function (context) {
                    return `${context.label}: ${new Intl.NumberFormat(
                      t("message.global.date_language"),
                      { style: "currency", currency: "BRL" }
                    ).format(context.parsed)}`;
                  },
                },
              },
              datalabels: {
                display: true,
                align: "center",
                backgroundColor: "#0000001A",
                borderRadius: 3,
                color: "white",
                font: { size: 12 },
                formatter: (value, ctx) => {
                  const total = ctx.chart.getDatasetMeta(0).total;
                  return ((value * 100) / total).toFixed(0) + "%";
                },
              },
              centerTotal: {
                display: true,
                fontSize: 20,
                subFontSize: 12,
                color: darkMode.value ? "white" : "black",
                subColor: darkMode.value ? "#D1D5DB" : "#6B7280",
                subtext: "Total",
                valueFormatter: () =>
                  total.toLocaleString(t("message.global.date_language"), {
                    style: "currency",
                    currency: "BRL"
                  })
              }
            }
          },
        };

        store.state.categoriesChartData = res.data;
        store.state.loading_categoriesChart = false;
        loaded_itens.push("CategoriesChart");
      }
    })
    .catch((err) => console.error(err));
};

const getObjetives = async () => {
  axios
    .get("/dashboard/objetives")
    .then((res) => {
      if (res.status === 200) {
        store.state.objetives = res.data;
        store.state.loading_objetives = false;
        loaded_itens.push("Objetives");
      }
    })
    .catch((err) => console.error(err));
};

const isMobile = () => {
  const isMobileDevice = window.navigator.userAgent.toLowerCase().includes("mobi");
  const isIpadDevice = window.navigator.userAgent.toLowerCase().includes("ipad");
  return isMobileDevice && !isIpadDevice;
};

const getUserData = async () => {
  axios
    .get("users/data")
    .then((res) => {
      if (res.status === 200) {
        cookie.add("user_agive_email", res.data.email, 86400);
        cookie.add("user_agive_name", res.data.name, 86400);

        if (res.data.darkmode === "Yes") {
          store.state.darkMode = true;
          setSidebarType("bg-default");
          activateDarkMode();
        } else {
          store.state.darkMode = false;
          setSidebarType("bg-white");
          deactivateDarkMode();
        }

        let full_name = res.data.name.split(' ');

        store.state.welcomeMessage = t('message.hello')+ ', ' + full_name[0] + "!";
      }
    })
    .catch((err) => console.error(err));
};

const getValuesAnnual = async () => {
  axios
    .get("dashboard/values-annual/" + store.state.InOutAnnualChartYear)
    .then((res) => {
      const labels = [
        meses[0], meses[1], meses[2], meses[3], meses[4], meses[5],
        meses[6], meses[7], meses[8], meses[9], meses[10], meses[11],
      ];

      store.state.InOutAnnualChart = {
        chartData: {
          labels,
          datasets: [
            {
              label: t("message.global.revenue"),
              backgroundColor: "#2dce89",
              borderRadius: 10,
              data: res.data.in,
            },
            {
              label: t("message.global.expense"),
              backgroundColor: "#f5365c",
              borderRadius: 10,
              data: res.data.out,
            },
          ],
        },
        chartOptions: {
          responsive: true,
          maintainAspectRatio: false,
          barPercentage: 0.7,
          plugins: {
            tooltip: {
              callbacks: {
                label: function (context) {
                  let label = context.dataset.label || "";
                  if (label) label += ": ";
                  if (context.raw !== null) {
                    label += new Intl.NumberFormat(
                      t("message.global.date_language"),
                      { style: "currency", currency: "BRL" }
                    ).format(context.raw);
                  }
                  return label;
                },
              },
            },
            legend: {
              display: true,
              labels: {
                color: darkMode.value ? "white" : "black",
                usePointStyle: true,
              },
            },
            datalabels: {
              display: false,
            },
          },
        },
      };

      store.state.loading_InOutAnnualChart = false;
      loaded_itens.push("InOutAnnualChart");
    })
    .catch((err) => console.error(err));
};

const slideDate = (direction) => {
  if (direction === "prev") {
    const prev_month = store.state.selectedMonth.index > 0 ? store.state.selectedMonth.index - 1 : 11;
    store.state.selectedYear =
      store.state.selectedMonth.index > 0 ? store.state.selectedYear : store.state.selectedYear - 1;
    store.state.selectedMonth = {
      index: prev_month,
      number: prev_month + 1,
      display: meses[prev_month],
    };
  } else if (direction === "next") {
    const next_month = store.state.selectedMonth.index < 11 ? store.state.selectedMonth.index + 1 : 0;
    store.state.selectedYear =
      store.state.selectedMonth.index < 11 ? store.state.selectedYear : store.state.selectedYear + 1;
    store.state.selectedMonth = {
      index: next_month,
      number: next_month + 1,
      display: meses[next_month],
    };
  }

  getValues();
  getCategories();
  getCards();
};

const clearFields = () => {
  store.state.transaction_value.value = 0;

  store.state.transation = {
    type: "",
    description: "",
    amount: 0,
    date: store.state.current_date,
    account: "",
    account_name: "",
    account_destiny: "",
    account_destiny_name: "",
    category: "",
    category_name: "",
    category_icon: "",
    category_color: "",
    recurrence_checked: false,
    recurrence: "",
    recurrence_period: "monthly",
    initial_installment: 1,
    current_installment: 1,
    end_installment: 1,
    payed: false,
    id: "",
    transaction_id: "",
    invoice_id: "",
    card_id: "",
    howUpdate: "",
    amount_paid: 0,
    ammount_unpaid: 0,
    observation: "",
    objetive: "",
  };

  store.state.suggestions = [];
};
</script>

<template>
  <div class="dashboard-insights-redesign pb-4 container-fluid">
    <div class="dashboard-topbar mb-3">
      <div class="row align-items-stretch g-3">
        <div class="col-12 col-lg-8">
          <div class="welcome-wrap redesign-hero" :class="darkMode ? 'darkmode' : ''">
            <span class="welcome-kicker redesign-kicker">Painel financeiro</span>
            <h4 class="welcome-title redesign-title">{{ store.state.welcomeMessage }}</h4>
          </div>
        </div>

        <div class="col-12 col-lg-4">
          <div class="period-selector redesign-period" :class="darkMode ? 'darkmode' : ''">
            <button class="period-btn redesign-period-btn" @click="slideDate('prev')">
              <i class="fal fa-chevron-left"></i>
            </button>
            <span>{{ store.state.selectedMonth.display }} {{ store.state.selectedYear }}</span>
            <button class="period-btn redesign-period-btn" @click="slideDate('next')">
              <i class="fal fa-chevron-right"></i>
            </button>
          </div>
        </div>
      </div>
    </div>

    <div class="stats-desktop-grid">
      <div id="mini-card-contas" class="stats-desktop-item">
        <mini-statistics-card
          :title="{ text: t('message.global.bank_account'), color: '' }"
          :value="{ text: store.state.values.balance, color: '' }"
          :future_value="{ text: store.state.values.balance_future, color: '' }"
          description=""
          :icon="{ component: 'fal fa-wallet fc-azul-petroleo', background: 'bg-contas', shape: 'rounded-5' }"
          :loading="store.state.loading_values"
          :rowReverse="true"
        />
      </div>

      <div id="mini-card-receitas" class="stats-desktop-item">
        <mini-statistics-card
          :title="{ text: t('message.global.revenue'), color: '' }"
          :value="{ text: store.state.values.in_payed, color: '' }"
          :future_value="{ text: store.state.values.in, color: '' }"
          description=""
          :icon="{ component: 'fal fa-plus fc-azul-petroleo', background: 'bg-success', shape: 'rounded-5' }"
          :loading="store.state.loading_values"
          :rowReverse="true"
        />
      </div>

      <div id="mini-card-despesas" class="stats-desktop-item">
        <mini-statistics-card
          :title="{ text: t('message.global.expense'), color: '' }"
          :value="{ text: store.state.values.out_payed, color: '' }"
          :future_value="{ text: store.state.values.out, color: '' }"
          description=""
          :icon="{ component: 'fal fa-minus text-white', background: 'bg-danger', shape: 'rounded-5' }"
          :loading="store.state.loading_values"
          :rowReverse="true"
        />
      </div>

      <div id="mini-card-cartoes" class="stats-desktop-item">
        <mini-statistics-card
          :title="{ text: t('message.global.card'), color: '' }"
          :value="{ text: store.state.values.cc_payed, color: '' }"
          :future_value="{ text: store.state.values.cc, color: '' }"
          description=""
          :icon="{ component: 'fal fa-credit-card fc-azul-petroleo', background: 'bg-cartoes', shape: 'rounded-5' }"
          :loading="store.state.loading_values"
          :rowReverse="true"
        />
      </div>
    </div>

    <div class="stats-mobile-carousel-shell mb-0">
      <div class="stats-mobile-carousel" ref="statsCarouselRef" @scroll="updateActiveStatSlide">
        <div class="stats-slide">
          <mini-statistics-card
            :title="{ text: t('message.global.bank_account'), color: '' }"
            :value="{ text: store.state.values.balance, color: '' }"
            :future_value="{ text: store.state.values.balance_future, color: '' }"
            description=""
            :icon="{ component: 'fal fa-wallet fc-azul-petroleo', background: 'bg-contas', shape: 'rounded-5' }"
            :loading="store.state.loading_values"
            :rowReverse="true"
          />
        </div>

        <div class="stats-slide">
          <mini-statistics-card
            :title="{ text: t('message.global.revenue'), color: '' }"
            :value="{ text: store.state.values.in_payed, color: '' }"
            :future_value="{ text: store.state.values.in, color: '' }"
            description=""
            :icon="{ component: 'fal fa-plus fc-azul-petroleo', background: 'bg-success', shape: 'rounded-5' }"
            :loading="store.state.loading_values"
            :rowReverse="true"
          />
        </div>

        <div class="stats-slide">
          <mini-statistics-card
            :title="{ text: t('message.global.expense'), color: '' }"
            :value="{ text: store.state.values.out_payed, color: '' }"
            :future_value="{ text: store.state.values.out, color: '' }"
            description=""
            :icon="{ component: 'fal fa-minus text-white', background: 'bg-danger', shape: 'rounded-5' }"
            :loading="store.state.loading_values"
            :rowReverse="true"
          />
        </div>

        <div class="stats-slide">
          <mini-statistics-card
            :title="{ text: t('message.global.card'), color: '' }"
            :value="{ text: store.state.values.cc_payed, color: '' }"
            :future_value="{ text: store.state.values.cc, color: '' }"
            description=""
            :icon="{ component: 'fal fa-credit-card fc-azul-petroleo', background: 'bg-cartoes', shape: 'rounded-5' }"
            :loading="store.state.loading_values"
            :rowReverse="true"
          />
        </div>
      </div>

      <div class="stats-mobile-dots" aria-label="Indicador do carrossel">
        <button
          v-for="index in 4"
          :key="index"
          type="button"
          class="stats-mobile-dot"
          :class="{ active: activeStatSlide === index - 1 }"
          @click="goToStatSlide(index - 1)"
          :aria-label="`Ir para card ${index}`"
        ></button>
      </div>
    </div>

    <div class="insights-panel mb-3">
      <div class="insights-panel-inner">
        <div class="insights-head">
          <div>
            <span class="insights-kicker">{{ t('message.dashboard_insights.insights_kicker') }}</span>
            <h4>{{ t('message.dashboard_insights.insights_title') }}</h4>
          </div>
          <i class="fal fa-bolt"></i>
        </div>

        <div class="insights-grid">
          <button
            v-for="(insight, index) in insights"
            :key="index"
            class="insight-card"
            :class="'tone-' + insight.tone"
            type="button"
            @click="handleInsightClick(insight)"
          >
            <div>
              <p class="insight-title">{{ insight.title }}</p>
              <p class="insight-body">{{ insight.body }}</p>
            </div>
            <span class="insight-tag">{{ insight.tag }}</span>
          </button>
        </div>
      </div>
    </div>

    <div class="row mansory-row g-3">
      <div id="minhas-contas" class="col-xxl-4 col-lg-6 col-md-6 order-lg-0">
        <div class="card redesign-card insight-style-card">
          <div class="p-3 pb-0 card-header redesign-card-header insight-style-header">
            <div class="d-flex justify-content-between">
              <h6 class="mb-3 text-white">{{ $t('message.home.my_accounts') }}</h6>
            </div>
          </div>

          <div v-if="store.state.loading_accounts" class="col-12">
            <div class="row align-items-center row-accounts">
              <div class="col-2 col-lg-1 ms-3"><SkeletonLoad :width="'45px'" :height="'45px'" :class="'rounded-5'" /></div>
              <div class="col-3 col-lg-4"><SkeletonLoad :width="'90px'" :height="'25px'" :class="'ms-4'" /></div>
              <div class="col-5 col-lg-6 text-end"><SkeletonLoad :width="'90px'" :height="'25px'" :class="'float-end'" /></div>
            </div>
          </div>

          <div v-else class="col-12">
            <Accounts
              v-for="(account, index) in store.state.accounts"
              :key="index"
              :bank="account.name"
              :balance="account.balance"
              :id="account.id"
              :icon="account.icon"
              :darkmode="darkMode"
            />
          </div>
        </div>
      </div>

      <div id="meus-cartoes" class="col-md-6 col-lg-6 col-xxl-4 mt-3 mb-lg-0 order-lg-2 order-xxl-3">
        <div class="card redesign-card insight-style-card">
          <div class="p-3 pb-0 card-header redesign-card-header insight-style-header">
            <div class="d-flex justify-content-between">
              <h6 class="mb-3 text-white">{{ $t('message.home.my_credit_cards') }}</h6>
            </div>
          </div>

          <div v-if="store.state.loading_cards" class="col-12">
            <div class="row align-items-center row-cards">
              <div class="col-5 d-flex align-items-center"><div><SkeletonLoad :width="'90px'" :height="'20px'" :class="'ms-4 mt-n1 mb-n1'" /></div></div>
              <div class="col-5 text-end"><SkeletonLoad :width="'90px'" :height="'20px'" :class="'ms-3'" /></div>
            </div>
          </div>

          <div v-else class="col-12 mt-3">
            <Cards
              v-for="(card, index) in store.state.cards_dashboard"
              :key="index"
              :name="card.name"
              :limit="card.limit"
              :value="card.current_invoice.current_amount"
              :before_value="card.current_invoice.before_amount"
              :due_date="card.current_invoice.due_date"
              :icon="card.icon"
              :id="card.current_invoice.id"
              :status="card.current_invoice.status"
              :sum="card.sum[0].current_amount"
              :fixes="card.future_fixed[0].amount ? card.future_fixed[0].amount : 0"
              :installments="card.future_Installments ? card.future_Installments[0].amount : 0"
              :darkmode="darkMode"
            />
          </div>

          <div class="col-12 text-center py-4" v-if="store.state.cards_dashboard.length === 0 && !store.state.loading_cards">
            <p>{{ t('message.global.zero_cards') }}</p>
          </div>
        </div>
      </div>

      <div id="meus-objetivos" class="col-md-6 col-lg-6 col-xxl-4 mb-md-0 mb-lg-0 order-lg-2 order-xxl-3">
        <div class="card redesign-card insight-style-card">
          <div class="p-3 pb-0 card-header redesign-card-header insight-style-header">
            <div class="d-flex justify-content-between">
              <h6 class="mb-3 text-white">{{ $t('message.home.my_objetives') }}</h6>
            </div>
          </div>

          <div v-if="store.state.loading_objetives" class="col-12">
            <div class="row align-items-center row-cards">
              <div class="col-5 d-flex align-items-center"><div><SkeletonLoad :width="'90px'" :height="'20px'" :class="'ms-4 mt-n1 mb-n1'" /></div></div>
              <div class="col-5 text-end"><SkeletonLoad :width="'90px'" :height="'20px'" :class="'ms-3'" /></div>
            </div>
          </div>

          <div v-else class="col-12 mt-3">
            <ObjetivesRowDashboard
              v-for="(objetive, index) in store.state.objetives"
              :key="index"
              :objetive="objetive"
              :darkmode="darkMode"
            />
          </div>

          <div class="col-12 text-center py-4" v-if="store.state.objetives.length === 0 && !store.state.loading_objetives">
            <p>{{ $t('message.global.zero_objetives') }}</p>
          </div>
        </div>
      </div>

      <div id="minhas-despesas-receitas" class="col-12 col-md-6 col-lg-6 col-xxl-4 mt-lg-3 order-lg-3 order-xxl-2">
        <div class="card redesign-card insight-style-card">
          <div class="pt-3 pb-0 card-header redesign-card-header insight-style-header">
            <div class="d-flex justify-content-between">
              <h6 class="mb-3 text-white">{{ $t('message.home.pending_transactions') }}</h6>
            </div>

            <ul class="nav nav-tabs no-border mb-n3 mb-md-0" id="transactions-tab" role="tablist">
              <li class="nav-item" role="presentation">
                <button class="nav-link active despesa" :class="darkMode ? 'darkmode' : ''" id="out-transactions-tab" data-bs-toggle="tab" data-bs-target="#out-transactions" type="button" role="tab" aria-controls="out-transactions" aria-selected="true">{{ $t('message.global.expense', 2) }}</button>
              </li>
              <li class="nav-item" role="presentation">
                <button class="nav-link" :class="darkMode ? 'darkmode' : ''" id="in-transactions-tab" data-bs-toggle="tab" data-bs-target="#in-transactions" type="button" role="tab" aria-controls="in-transactions" aria-selected="false">{{ $t('message.global.revenue', 2) }}</button>
              </li>
            </ul>
          </div>

          <div class="tab-content">
            <div class="tab-pane fade show active" id="out-transactions" role="tabpanel" aria-labelledby="out-transactions-tab">
              <div v-if="store.state.loading_transactions" class="col-12">
                <div class="row align-items-center row-transactions">
                  <div class="col-2 col-lg-1 ms-3 text-center"><SkeletonLoad :width="'45px'" :height="'45px'" /></div>
                  <div class="col-7 col-lg-8">
                    <div class="row align-items-center">
                      <div class="col-12 col-lg-6"><SkeletonLoad :width="'80px'" :height="'20px'" :class="'ms-2'" /><SkeletonLoad :width="'120px'" :height="'20px'" :class="'ms-2 mt-1'" /></div>
                      <div class="col-12 col-lg-6 text-lg-center"><SkeletonLoad :width="'120px'" :height="'20px'" /></div>
                    </div>
                  </div>
                </div>
              </div>

              <div class="col-12 text-center mt-4" v-if="store.state.transactions_out.length === 0 && !store.state.loading_transactions">
                <p>{{ $t('message.global.zero_transactions') }}</p>
              </div>

              <div v-if="!store.state.loading_transactions && store.state.transactions_out.length > 0" class="contas mb-3">
                <Transactions v-for="(btp, index) in store.state.transactions_out" :key="index" :transaction="btp" @pay="payTransation" :darkmode="darkMode" />
              </div>
            </div>

            <div class="tab-pane fade" id="in-transactions" role="tabpanel" aria-labelledby="in-transactions-tab">
              <div v-if="store.state.loading_transactions" class="col-12">
                <div class="row align-items-center row-transactions">
                  <div class="col-2 col-lg-1 ms-3 text-center"><SkeletonLoad :width="'45px'" :height="'45px'" /></div>
                  <div class="col-7 col-lg-8">
                    <div class="row align-items-center">
                      <div class="col-12 col-lg-6"><SkeletonLoad :width="'80px'" :height="'20px'" :class="'ms-2'" /><SkeletonLoad :width="'120px'" :height="'20px'" :class="'ms-2 mt-1'" /></div>
                      <div class="col-12 col-lg-6 text-lg-center"><SkeletonLoad :width="'120px'" :height="'20px'" /></div>
                    </div>
                  </div>
                </div>
              </div>

              <div class="col-12 text-center mt-4" v-if="store.state.transactions_in.length === 0 && !store.state.loading_transactions">
                <p>{{ $t('message.global.zero_transactions') }}</p>
              </div>

              <div v-if="!store.state.loading_transactions && store.state.transactions_in.length > 0" class="contas mb-3">
                <Transactions v-for="(btp, index) in store.state.transactions_in" :key="index" :transaction="btp" @pay="payTransation" :darkmode="darkMode" />
              </div>
            </div>
          </div>
        </div>
      </div>

      <div id="despesas-categoria" class="col-12 col-md-6 col-lg-6 col-xxl-4 mt-md-3 mt-lg-3 order-lg-1">
        <div class="card redesign-card insight-style-card">
          <div class="p-3 pb-0 card-header redesign-card-header insight-style-header">
            <div class="d-flex justify-content-between">
              <h6 class="mb-3 text-white">{{ $t('message.home.expenses_by_category') }}</h6>
            </div>
          </div>

          <div class="col-12">
            <div class="row">
              <div class="col-lg-9 col-xxl-11 mb-3 mt-3 mx-auto chart-holder chart-holder--donut">
                <DonutChart
                  v-if="store.state.categoriesChartData.length > 0 && !store.state.loading_categoriesChart"
                  :chartData="store.state.categoriesChart.chartData"
                  :chartOptions="store.state.categoriesChart.chartOptions"
                />
                <SkeletonLoad
                  v-if="store.state.loading_categoriesChart"
                  :width="'250px'"
                  :height="'250px'"
                  :class="'rounded-5 mx-auto mb-4'"
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      <div id="receitas-despesas" class="col-12 col-md-6 col-lg-6 col-xxl-4 mt-md-3 mt-lg-3 order-lg-1">
        <div class="card redesign-card insight-style-card">
          <div class="p-3 pb-0 card-header redesign-card-header insight-style-header">
            <div class="d-flex justify-content-between">
              <h6 class="mb-3 text-white">{{ $t('message.global.revenue', 2) }} x {{ $t('message.global.expense', 2) }}</h6>
            </div>
          </div>

          <div class="col-12">
            <div class="row">
              <div class="col-11 mt-3 mb-3 mx-auto chart-holder chart-holder--bar">
                <BarChart
                  v-if="!store.state.loading_values"
                  :chartData="store.state.InOutChart.chartData"
                  :chartOptions="store.state.InOutChart.chartOptions"
                />
                <SkeletonLoad
                  v-if="store.state.loading_values"
                  :width="'250px'"
                  :height="'250px'"
                  :class="'rounded-5 mx-auto mb-4'"
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      <div id="receitas-despesas-anual" class="col-12 mt-lg-3 mb-5 mb-xl-5 order-lg-1">
        <div class="card redesign-card insight-style-card">
          <div class="p-3 pb-0 card-header redesign-card-header insight-style-header">
            <div class="d-flex justify-content-between">
              <div class="col-12">
                <div class="row">
                  <div class="col-8 col-lg-10">
                    <h6 class="mb-3 text-white">{{ $t('message.global.revenue', 2) }} x {{ $t('message.global.expense', 2) }} - Anual</h6>
                  </div>
                  <div class="col-4 col-lg-2">
                    <select class="form-control form-control-sm redesign-select" v-model="store.state.InOutAnnualChartYear" @change="getValuesAnnual()" :class="{ 'darkmode': darkMode }">
                      <option v-for="n in 50" :key="n" :value="n + 2023">{{ n + 2023 }}</option>
                    </select>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div class="col-12">
            <div class="row">
              <div class="col-11 mt-3 mb-3 mx-auto chart-holder chart-holder--annual">
                <BarChart
                  v-if="!store.state.loading_InOutAnnualChart"
                  :chartData="store.state.InOutAnnualChart.chartData"
                  :chartOptions="store.state.InOutAnnualChart.chartOptions"
                />
                <SkeletonLoad
                  v-if="store.state.loading_InOutAnnualChart"
                  :width="'250px'"
                  :height="'250px'"
                  :class="'mx-auto mb-4'"
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

  </div>
</template>

<style>
.dashboard-insights-redesign {
  overflow-x: hidden;
}

.redesign-hero {
  background: linear-gradient(135deg, #12395f 0%, #1d517f 55%, #2f6ea1 100%);
  border-radius: 30px;
  padding: 1.4rem 1.5rem;
  color: #fff;
  box-shadow: 0 22px 48px rgba(18, 57, 95, 0.16);
  position: relative;
  overflow: hidden;
}

.redesign-hero::after {
  content: "";
  position: absolute;
  right: -60px;
  top: -40px;
  width: 220px;
  height: 220px;
  border-radius: 999px;
  background: rgba(255,255,255,0.08);
  filter: blur(10px);
}

.redesign-kicker,
.insights-kicker {
  display: inline-flex;
  font-size: 0.72rem;
  font-weight: 800;
  letter-spacing: 0.18em;
  text-transform: uppercase;
  opacity: 0.78;
}

.redesign-title {
  margin: 0.45rem 0 0;
  font-size: 2rem;
  font-weight: 900;
  line-height: 1.06;
  color: #fff;
}

.redesign-period {
  height: 100%;
  min-height: 84px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.9rem;
  border-radius: 24px;
  background: linear-gradient(135deg, #12395f 0%, #1d517f 100%) !important;
  color: #fff !important;
  box-shadow: 0 14px 34px rgba(18, 57, 95, 0.12);
  border: 1px solid rgba(255,255,255,0.12);
  font-weight: 700;
}

.redesign-period-btn {
  width: 38px;
  height: 38px;
  border: none;
  border-radius: 999px;
  background: rgba(255,255,255,0.16);
  color: inherit;
}

.insights-panel-inner {
  background: rgba(255,255,255,0.96);
  border: 1px solid rgba(15, 23, 42, 0.06);
  border-radius: 28px;
  box-shadow: 0 10px 30px rgba(15, 23, 42, 0.06), 0 2px 8px rgba(15, 23, 42, 0.03);
  padding: 1.25rem;
}

.insights-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  margin-bottom: 1rem;
}

.insights-head h4 {
  margin: 0.35rem 0 0;
  font-size: 1.8rem;
  font-weight: 800;
  color: #31486d;
}

.insights-grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 0.85rem;
}

.insight-card {
  width: 100%;
  border: 1px solid rgba(15, 23, 42, 0.05);
  border-radius: 22px;
  background: rgba(18,57,95,0.03);
  padding: 1rem;
  text-align: left;
  display: flex;
  flex-direction: column;
  gap: 0.85rem;
}

.insight-title {
  margin: 0;
  font-size: 1rem;
  font-weight: 800;
  color: #31486d;
}

.insight-body {
  margin: 0.2rem 0 0;
  color: #627693;
  font-size: 0.92rem;
}

.insight-tag {
  align-self: flex-start;
  border-radius: 999px;
  padding: 0.45rem 0.75rem;
  font-size: 0.8rem;
  font-weight: 700;
}

.tone-danger .insight-tag { background: rgba(245,54,92,0.12); color: #c9485b; }
.tone-warning .insight-tag { background: rgba(209,133,54,0.14); color: #b46d2f; }
.tone-info .insight-tag { background: rgba(18,57,95,0.08); color: #12395f; }
.tone-success .insight-tag { background: rgba(45,206,137,0.14); color: #198754; }

.redesign-card {
  background: rgba(255, 255, 255, 0.94) !important;
  border: 1px solid rgba(15, 23, 42, 0.06) !important;
  border-radius: 28px !important;
  box-shadow: 0 10px 30px rgba(15, 23, 42, 0.06), 0 2px 8px rgba(15, 23, 42, 0.03) !important;
  overflow: hidden;
}

.redesign-card-header {
  background: linear-gradient(135deg, #1d4f7c 0%, #295f91 100%) !important;
  border-bottom: none !important;
  padding-top: 0.85rem !important;
  padding-bottom: 0.75rem !important;
}

.redesign-card-header h6 {
  font-size: 0.9rem !important;
  font-weight: 800 !important;
  letter-spacing: 0.01em !important;
  margin-bottom: 0 !important;
}

.contas {
  max-height: 500px;
  overflow-x: hidden;
  overflow-y: auto;
  padding-top: 0.35rem;
  padding-bottom: 0.35rem;
}

.chart-holder {
  border-radius: 22px;
  background: linear-gradient(180deg, #ffffff 0%, #f8fafc 100%);
  border: 1px solid rgba(148, 163, 184, 0.12);
  padding: 1rem;
  box-shadow: inset 0 1px 0 rgba(255,255,255,0.6);
}

.chart-holder--donut { min-height: 290px; }
.chart-holder--bar { min-height: 250px; }
.chart-holder--annual { min-height: 400px; }

.row-accounts,
.row-cards,
.row-objetives,
.row-transactions {
  padding-top: 1rem;
  padding-bottom: 1rem;
}

.row-accounts:not(:last-child),
.row-cards:not(:last-child),
.row-objetives:not(:last-child),
.row-transactions:not(:last-child) {
  border-bottom: 1px solid rgba(15, 23, 42, 0.05);
}

#transactions-tab .nav-link.active {
  border-bottom: 2px solid #20c997 !important;
  border-top: none !important;
  border-left: none !important;
  border-right: none !important;
}

#transactions-tab .nav-link.active.despesa {
  border-bottom: 2px solid #f5365c !important;
}

#transactions-tab .nav-link.active.darkmode {
  border-bottom: 2px solid #20c997 !important;
  border-top: none !important;
  border-left: none !important;
  border-right: none !important;
  background-color: rgba(255, 255, 255, 0.13);
}

#transactions-tab .nav-link.active.despesa.darkmode {
  border-bottom: 2px solid #f5365c !important;
  background-color: rgba(255, 255, 255, 0.13);
}

#transactions-tab .nav-link {
  border: none !important;
  color: #fff;
  width: 100%;
  font-weight: 700;
}

#transactions-tab .nav-item {
  width: 50%;
}

.bg-contas {
  background: #6FA8DC;
  color: #6FA8DC;
}

.bg-cartoes {
  background: #9575CD;
  color: #9575CD;
}

.rounded-5 {
  border-radius: 0.95rem;
}

.redesign-select {
  border-radius: 14px !important;
  border: 1px solid rgba(255,255,255,0.18) !important;
  background: rgba(255,255,255,0.14) !important;
  color: #fff !important;
  font-weight: 700;
}

@media (max-width: 991.98px) {
  .redesign-title {
    font-size: 1.45rem;
  }

  .redesign-period {
    min-height: 72px;
  }

  .insights-grid {
    grid-template-columns: 1fr;
  }
}

/* Cards no mesmo estilo do bloco de insights */
.insight-style-card {
  background: rgba(255,255,255,0.96) !important;
  border: 1px solid rgba(15, 23, 42, 0.06) !important;
  border-radius: 28px !important;
  box-shadow: 0 10px 30px rgba(15, 23, 42, 0.06), 0 2px 8px rgba(15, 23, 42, 0.03) !important;
  overflow: hidden;
}

.insight-style-header {
  background: transparent !important;
  border-bottom: none !important;
  padding-top: 1.1rem !important;
  padding-bottom: 0.4rem !important;
}

.insight-style-header h6 {
  color: #31486d !important;
  font-size: 1.05rem !important;
  font-weight: 800 !important;
  letter-spacing: 0 !important;
  margin-bottom: 0 !important;
}

.insight-style-card .chart-holder {
  background: rgba(18,57,95,0.03);
  border: 1px solid rgba(15, 23, 42, 0.05);
  border-radius: 22px;
  box-shadow: none;
}

.insight-style-card .contas {
  padding: 0.35rem 0.35rem 0.5rem;
}

.insight-style-card .row-accounts,
.insight-style-card .row-cards,
.insight-style-card .row-objetives,
.insight-style-card .row-transactions {
  padding-top: 1rem;
  padding-bottom: 1rem;
}

.insight-style-card .row-accounts:not(:last-child),
.insight-style-card .row-cards:not(:last-child),
.insight-style-card .row-objetives:not(:last-child),
.insight-style-card .row-transactions:not(:last-child) {
  border-bottom: 1px solid rgba(15, 23, 42, 0.05);
}

.insight-style-card #transactions-tab,
.insight-style-card .nav-tabs {
  margin-top: 0.75rem;
  background: rgba(18,57,95,0.05);
  border-radius: 999px;
  padding: 0.25rem;
}

.insight-style-card #transactions-tab .nav-link {
  color: #31486d !important;
  border-radius: 999px !important;
  font-weight: 700;
}

.insight-style-card #transactions-tab .nav-link.active {
  background: #20c997 !important;
  color: #fff !important;
  border-bottom: none !important;
}

.insight-style-card #transactions-tab .nav-link.active.despesa {
  background: #d14d63 !important;
  color: #fff !important;
}

.insight-style-card .redesign-select {
  background: rgba(18,57,95,0.08) !important;
  color: #12395f !important;
  border: 1px solid rgba(18,57,95,0.08) !important;
}

.darkmode .insight-style-card {
  background: rgba(15,23,42,0.92) !important;
  border-color: rgba(255,255,255,0.06) !important;
  box-shadow: 0 12px 28px rgba(0,0,0,0.28) !important;
}

.darkmode .insight-style-header {
  background: transparent !important;
}

.darkmode .insight-style-header h6 {
  color: #f8fafc !important;
}

.darkmode .insight-style-card .chart-holder {
  background: rgba(255,255,255,0.03);
  border-color: rgba(255,255,255,0.06);
}

.darkmode .insight-style-card #transactions-tab,
.darkmode .insight-style-card .nav-tabs {
  background: rgba(255,255,255,0.05);
}

.darkmode .insight-style-card #transactions-tab .nav-link {
  color: #e5e7eb !important;
}

.darkmode .insight-style-card #transactions-tab .nav-link.active {
  background: rgba(255,255,255,0.10) !important;
  color: #fff !important;
}

.darkmode .insight-style-card #transactions-tab .nav-link.active.despesa {
  background: #d14d63 !important;
  color: #fff !important;
}

.darkmode .insight-style-card .redesign-select {
  background: rgba(255,255,255,0.08) !important;
  color: #fff !important;
  border-color: rgba(255,255,255,0.08) !important;
}



/* Desktop grid dos mini cards */
.stats-desktop-grid {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 1rem;
}

.stats-desktop-item,
.stats-slide {
  min-width: 0;
}

.stats-desktop-item > *,
.stats-slide > * {
  width: 100%;
}

/* Mobile: carrossel full-width, sem card cortado */
.stats-mobile-carousel-shell {
  display: none;
}

@media (max-width: 767.98px) {
  .stats-desktop-grid {
    display: none !important;
  }

  .stats-mobile-carousel-shell {
    display: block;
    margin-left: -0.1rem;
    margin-right: -0.1rem;
  }

  .stats-mobile-carousel {
    display: flex;
    overflow-x: auto;
    overflow-y: hidden;
    gap: 0.9rem;
    padding: 0 0.1rem 0.35rem;
    scroll-snap-type: x mandatory;
    -webkit-overflow-scrolling: touch;
    scrollbar-width: none;
  }

  .stats-mobile-carousel::-webkit-scrollbar {
    display: none;
  }

  .stats-mobile-carousel .stats-slide {
    flex: 0 0 100%;
    width: 100%;
    min-width: 100%;
    max-width: 100%;
    scroll-snap-align: start;
  }

  .stats-mobile-carousel .stats-slide .card,
  .stats-mobile-carousel .stats-slide .mini-statistics-card {
    margin-bottom: 0 !important;
    border-radius: 24px !important;
  }
}

@media (min-width: 768px) {
  .stats-mobile-carousel-shell {
    display: none !important;
  }
}



/* Ajustes do card mobile: mais respiro e indicador lateral */
@media (max-width: 767.98px) {
  .stats-mobile-carousel .mini-stat-content {
    padding-left: 0.7rem !important;
  }

  .stats-mobile-carousel .mini-stat-title {
    margin-left: 0.05rem !important;
  }

  .stats-mobile-carousel .mini-stat-predicted,
  .stats-mobile-carousel .mini-stat-future {
    margin-left: 0.05rem !important;
  }

  .stats-mobile-carousel-shell {
    position: relative;
  }

  .stats-mobile-carousel-shell::after {
    content: "›";
    position: absolute;
    right: 0.35rem;
    top: 50%;
    transform: translateY(-50%);
    width: 28px;
    height: 28px;
    border-radius: 999px;
    display: flex;
    align-items: center;
    justify-content: center;
    background: rgba(255,255,255,0.92);
    color: #7b8faa;
    font-size: 1.15rem;
    font-weight: 700;
    box-shadow: 0 6px 16px rgba(15,23,42,0.08);
    pointer-events: none;
  }

  .darkmode .stats-mobile-carousel-shell::after {
    background: rgba(15,23,42,0.92);
    color: #cbd5e1;
  }
}



/* Indicador lateral em fade para o carousel mobile */
@media (max-width: 767.98px) {
  .stats-mobile-carousel-shell::after {
    content: "";
    position: absolute;
    right: 0;
    top: 8px;
    width: 28px;
    height: calc(100% - 16px);
    border-radius: 0 20px 20px 0;
    background: linear-gradient(
      90deg,
      rgba(245, 241, 229, 0) 0%,
      rgba(245, 241, 229, 0.35) 60%,
      rgba(245, 241, 229, 0.75) 100%
    );
    pointer-events: none;
    z-index: 1;
  }

  .stats-mobile-carousel-shell::before {
    content: "›";
    position: absolute;
    right: 0.4rem;
    top: 50%;
    transform: translateY(-50%);
    font-size: 0.9rem;
    color: #9fb1c7;
    z-index: 2;
    pointer-events: none;
    opacity: 0.7;
  }

  .darkmode .stats-mobile-carousel-shell::after {
    background: linear-gradient(
      90deg,
      rgba(15, 23, 42, 0) 0%,
      rgba(15, 23, 42, 0.28) 60%,
      rgba(15, 23, 42, 0.68) 100%
    );
  }

  .darkmode .stats-mobile-carousel-shell::before {
    color: #cbd5e1;
  }
}



/* Remove fade e adiciona indicador de bolinhas no mobile */
@media (max-width: 767.98px) {
  .stats-mobile-carousel-shell::before,
  .stats-mobile-carousel-shell::after {
    content: none !important;
    display: none !important;
  }

  .stats-mobile-carousel-shell {
    position: relative;
    padding-bottom: 1.1rem;
  }

  .stats-mobile-carousel-shell .stats-mobile-carousel {
    position: relative;
  }

  .stats-mobile-carousel-shell .stats-mobile-carousel::after {
    content: "●  ●  ●  ●";
    display: block;
    width: 100%;
    text-align: center;
    font-size: 0.5rem;
    letter-spacing: 0.35rem;
    color: #cbd5e1;
    margin-top: 0.55rem;
    line-height: 1;
  }

  .darkmode .stats-mobile-carousel-shell .stats-mobile-carousel::after {
    color: #475569;
  }
}



/* Indicador real de bolinhas do carrossel mobile */
.stats-mobile-dots {
  display: none;
}

@media (max-width: 767.98px) {
  .stats-mobile-dots {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 0.45rem;
    margin-top: 0.6rem;
  }

  .stats-mobile-dot {
    width: 7px;
    height: 7px;
    border: 0;
    border-radius: 999px;
    background: #cbd5e1;
    opacity: 1;
    padding: 0;
    transition: all 0.2s ease;
  }

  .stats-mobile-dot.active {
    width: 22px;
    background: #12395f;
  }

  .darkmode .stats-mobile-dot {
    background: #475569;
  }

  .darkmode .stats-mobile-dot.active {
    background: #e2e8f0;
  }

  .stats-mobile-carousel-shell::before,
  .stats-mobile-carousel-shell::after,
  .stats-mobile-carousel::after {
    content: none !important;
    display: none !important;
  }
}



/* Setas do seletor de período nas extremidades */
.redesign-period {
  display: flex;
  align-items: center;
  justify-content: space-between !important;
  padding: 0.6rem 0.9rem;
  gap: 0.5rem;
}

.redesign-period span {
  flex: 1;
  text-align: center;
  font-weight: 800;
}

.redesign-period .period-btn,
.redesign-period .redesign-period-btn,
.redesign-period button {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex: 0 0 auto;
}

@media (max-width: 767.98px) {
  .redesign-period {
    padding: 0.55rem 0.7rem;
  }

  #receitas-despesas-anual{
    margin-bottom: 150px !important;
  }
}

@media (max-width: 1199.98px) {
  .stats-desktop-grid {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 1rem;
  }
}

</style>
